/*
 * Tema claro/escuro do ERP.
 *
 * A preferência fica em localStorage ('tema': 'claro' | 'escuro' | 'auto');
 * 'auto' segue o sistema operacional. O CSS está em
 * src/shared/assets/css/tema-escuro.scss e é ativado pelo atributo
 * data-tema="escuro" no <html>. O public/layouts/adminlte/index.html aplica o
 * atributo antes do primeiro paint com a mesma regra (sem flash claro); aqui
 * fica a API para a interface e a escuta da mudança no sistema.
 *
 * Telas públicas (login, cadastro, impressão) ficam fora do tema: o router
 * chama suspendeTemaNoDocumento() nelas e inicializaTema() nas demais.
 */
export const TEMA_CLARO = 'claro'
export const TEMA_ESCURO = 'escuro'
export const TEMA_AUTO = 'auto'
export const EVENTO_TEMA = 'tema-alterado'

const TEMAS = [TEMA_CLARO, TEMA_ESCURO, TEMA_AUTO]
const CHAVE = 'tema'
const ATRIBUTO = 'data-tema'

let escutandoSistema = false

// Telas fora do tema (login, cadastro, impressão, meta.temaClaro). O router
// liga e desliga isto. Enquanto está ligado, nada pinta o documento de escuro:
// sem essa trava o interruptor do menu do usuário, ou uma troca de tema do
// sistema no modo 'auto', reaplicava o escuro numa tela desenhada para o claro
// e ela ficava meio clara meio escura (visto no ClickNotas em
// /usuarios/redefinir_senha, 2026-09-17). A preferência continua sendo salva.
let suspenso = false

const mediaEscuro = () => {
    if (typeof window === 'undefined' || !window.matchMedia) {
        return null
    }
    return window.matchMedia('(prefers-color-scheme: dark)')
}

// Preferência salva. Sem nada salvo (ou storage bloqueado), claro.
export const pegaTema = () => {
    try {
        const tema = localStorage.getItem(CHAVE)
        return TEMAS.includes(tema) ? tema : TEMA_CLARO
    } catch (e) {
        return TEMA_CLARO
    }
}

// Resolve 'auto' para o que vale agora.
export const temaEfetivo = (tema = pegaTema()) => {
    if (tema !== TEMA_AUTO) {
        return tema
    }
    const media = mediaEscuro()
    return media && media.matches ? TEMA_ESCURO : TEMA_CLARO
}

export const temaEscuroAtivo = () => {
    return document.documentElement.getAttribute(ATRIBUTO) === TEMA_ESCURO
}

const aplicaNoDocumento = (tema) => {
    const efetivo = temaEfetivo(tema)
    if (efetivo === TEMA_ESCURO && !suspenso) {
        document.documentElement.setAttribute(ATRIBUTO, TEMA_ESCURO)
    } else {
        document.documentElement.removeAttribute(ATRIBUTO)
    }
    window.dispatchEvent(new CustomEvent(EVENTO_TEMA, { detail: { tema, efetivo } }))
    return efetivo
}

// Salva e aplica. Devolve o tema salvo.
export const aplicaTema = (tema) => {
    const valido = TEMAS.includes(tema) ? tema : TEMA_CLARO
    try {
        localStorage.setItem(CHAVE, valido)
    } catch (e) {
        // storage indisponível: vale só para esta página
    }
    aplicaNoDocumento(valido)
    return valido
}

// Interruptor simples: escuro <-> claro (sai do 'auto').
export const alternaTema = () => {
    return aplicaTema(temaEfetivo() === TEMA_ESCURO ? TEMA_CLARO : TEMA_ESCURO)
}

// Aplica a preferência salva e passa a seguir o sistema quando for 'auto'.
export const inicializaTema = () => {
    suspenso = false
    aplicaNoDocumento(pegaTema())
    const media = mediaEscuro()
    if (!media || escutandoSistema) {
        return
    }
    escutandoSistema = true
    const aoMudar = () => {
        if (pegaTema() === TEMA_AUTO) {
            aplicaNoDocumento(TEMA_AUTO)
        }
    }
    if (media.addEventListener) {
        media.addEventListener('change', aoMudar)
    } else if (media.addListener) {
        media.addListener(aoMudar)
    }
}

// Tira o tema da página sem mexer na preferência salva (telas públicas).
export const suspendeTemaNoDocumento = () => {
    suspenso = true
    document.documentElement.removeAttribute(ATRIBUTO)
}

// A tela atual aceita tema escuro? (o interruptor some nas que não aceitam)
export const temaDisponivelNaTela = () => {
    return !suspenso
}
